/**
 * Shared dashboard-widget primitives.
 * All widgets render inside a `<Widget>` shell that gives them:
 *   - Uniform header (title, optional action badge, optional help tooltip)
 *   - Loading skeleton state
 *   - Empty state
 *   - Standardised padding + card styling
 */
import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@erp/ui';
import {
  Area, AreaChart, Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip,
  XAxis, YAxis,
} from 'recharts';

interface WidgetProps {
  title: string;
  description?: string;
  action?: ReactNode;
  isLoading?: boolean;
  isEmpty?: boolean;
  emptyLabel?: string;
  className?: string;
  children?: ReactNode;
}

export function Widget({
  title,
  description,
  action,
  isLoading,
  isEmpty,
  emptyLabel,
  className,
  children,
}: WidgetProps) {
  return (
    <Card className={`h-full flex flex-col ${className ?? ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-sm font-semibold">{title}</CardTitle>
            {description && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{description}</p>
            )}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      </CardHeader>
      <CardContent className="pt-0 flex-1 flex flex-col justify-center">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : isEmpty ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {emptyLabel ?? '—'}
          </p>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

// ---- Formatters (re-exported for widget use) ----

export function fmtMoney(n: number, decimals: 0 | 3 = 0): string {
  return n.toLocaleString('fr-TN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function fmtCount(n: number): string {
  return n.toLocaleString('fr-TN');
}

export function fmtDateFr(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

export function isoMonthStart(year: number, month: number): string {
  return new Date(year, month, 1).toISOString().slice(0, 10);
}

export function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

// ---- Top list row (used by many widgets) ----

export interface TopListRow {
  id: string;
  label: string;
  sublabel?: string;
  value: number;
  unit?: string;
  decimals?: 0 | 3;
}

export function TopList({ rows, emptyLabel }: { rows: TopListRow[]; emptyLabel?: string }) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        {emptyLabel ?? '—'}
      </p>
    );
  }
  return (
    <ul className="space-y-1.5">
      {rows.map((r, i) => (
        <li
          key={r.id}
          className="flex items-center justify-between gap-2 text-sm border-b last:border-b-0 pb-1.5 last:pb-0"
        >
          <span className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-xs text-muted-foreground shrink-0 tabular-nums w-5">
              {i + 1}.
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{r.label}</span>
              {r.sublabel && (
                <span className="block truncate text-xs text-muted-foreground">
                  {r.sublabel}
                </span>
              )}
            </span>
          </span>
          <span className="tabular-nums font-medium shrink-0">
            {fmtMoney(r.value, r.decimals ?? 0)}
            {r.unit && <span className="ms-1 text-xs text-muted-foreground">{r.unit}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---- Horizontal bar list (replaces TopList for visual top-N) ----

export function HBarList({
  rows,
  emptyLabel,
  barClassName = 'bg-primary',
}: {
  rows: TopListRow[];
  emptyLabel?: string;
  barClassName?: string;
}) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        {emptyLabel ?? '—'}
      </p>
    );
  }
  const max = Math.max(...rows.map((r) => Math.abs(r.value)), 1);
  return (
    <ul className="space-y-2.5">
      {rows.map((r) => {
        const pct = Math.max(2, (Math.abs(r.value) / max) * 100);
        return (
          <li key={r.id} className="text-sm">
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <span className="truncate flex-1 min-w-0">
                <span className="font-medium">{r.label}</span>
                {r.sublabel && (
                  <span className="text-xs text-muted-foreground ms-1.5">{r.sublabel}</span>
                )}
              </span>
              <span className="tabular-nums font-medium shrink-0 text-xs">
                {fmtMoney(r.value, r.decimals ?? 0)}
                {r.unit && <span className="ms-1 text-muted-foreground">{r.unit}</span>}
              </span>
            </div>
            <div className="h-1.5 bg-muted/60 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-[width] duration-500 ${barClassName}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ---- Sparkline (tiny area chart) ----

export function Sparkline({
  data,
  height = 44,
  label,
}: {
  data: number[];
  color?: string;
  height?: number;
  label?: string;
}) {
  const points = data.map((v, i) => ({ i, v }));
  const allZero = data.every((v) => v === 0);

  if (allZero || data.length < 2) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center text-xs text-muted-foreground"
      >
        {label ?? '—'}
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={points} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
        <defs>
          <linearGradient id="sparkGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.3} />
            <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          type="monotone"
          dataKey="v"
          stroke="var(--primary)"
          strokeWidth={1.75}
          fill="url(#sparkGradient)"
          dot={false}
          activeDot={{ r: 3, fill: 'var(--primary)' }}
          isAnimationActive={false}
        />
        <Tooltip
          cursor={false}
          contentStyle={{
            background: 'var(--background)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 11,
            padding: '2px 6px',
          }}
          labelFormatter={() => ''}
          formatter={(v) => [fmtMoney(Number(v) || 0, 0), '']}
          separator=""
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ---- Mini bar chart (few labelled bars) ----

export interface MiniBarPoint {
  label: string;
  value: number;
  color?: string;
}

export function MiniBars({ bars, height = 120 }: { bars: MiniBarPoint[]; height?: number }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={bars} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
        <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis hide />
        <Tooltip
          cursor={{ fill: 'color-mix(in oklch, var(--muted) 30%, transparent)' }}
          contentStyle={{
            background: 'var(--background)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 11,
            padding: '4px 8px',
          }}
          formatter={(v) => [fmtMoney(Number(v) || 0, 0), '']}
          separator=""
        />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {bars.map((b, i) => (
            <Cell key={i} fill={b.color ?? 'var(--primary)'} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ---- Mini donut chart ----

export function MiniDonut({
  slices,
  centerLabel,
  height = 140,
}: {
  slices: { label: string; value: number; color: string }[];
  centerLabel?: string;
  height?: number;
}) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  if (total === 0) {
    return (
      <div style={{ height }} className="flex items-center justify-center text-sm text-muted-foreground">
        —
      </div>
    );
  }
  return (
    <div className="flex items-center gap-4" style={{ height }}>
      <div className="relative" style={{ width: height, height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              innerRadius="60%"
              outerRadius="95%"
              startAngle={90}
              endAngle={-270}
              stroke="none"
              isAnimationActive={false}
            >
              {slices.map((s, i) => (
                <Cell key={i} fill={s.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: 'var(--background)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 11,
                padding: '4px 8px',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        {centerLabel && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-lg font-semibold tabular-nums">{centerLabel}</span>
          </div>
        )}
      </div>
      <ul className="flex-1 space-y-1.5 text-xs min-w-0">
        {slices.map((s) => {
          const pct = (s.value / total) * 100;
          return (
            <li key={s.label} className="flex items-center gap-2">
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
                style={{ backgroundColor: s.color }}
              />
              <span className="min-w-0 flex-1 truncate">{s.label}</span>
              <span className="tabular-nums text-muted-foreground shrink-0">
                {fmtCount(s.value)} · {pct.toFixed(0)}%
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---- Big-stat primitive (single number, optional sub-stat) ----

export function BigStat({
  value,
  unit,
  sub,
  tone = 'neutral',
}: {
  value: string;
  unit?: string;
  sub?: string;
  tone?: 'neutral' | 'positive' | 'negative' | 'warning';
}) {
  const toneClass = {
    neutral:  'text-foreground',
    positive: 'text-emerald-600',
    negative: 'text-rose-600',
    warning:  'text-amber-600',
  }[tone];

  return (
    <div>
      <p className={`text-3xl font-semibold tabular-nums ${toneClass}`}>
        {value}
        {unit && <span className="ms-1 text-base font-normal text-muted-foreground">{unit}</span>}
      </p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}
